import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "@/assets/logo.png";
import { OptionCard } from "@/components/diagnostico/OptionCard";
import { Field, QuestionTitle } from "@/components/diagnostico/Field";
import {
  CARGAS,
  CUSTOS,
  formatCurrency,
  formatPhone,
  MAO_DE_OBRA,
  MARGENS,
  MOMENTOS,
  PERFIS,
  REGIMES,
  SETORES,
} from "@/lib/diagnostico";
import { checkEmailExists, createDiagnostico } from "@/lib/api";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Diagnóstico Tributário | Análise Executiva" },
      {
        name: "description",
        content:
          "Responda algumas perguntas rápidas e receba um diagnóstico tributário detalhado sobre carga, regime, margem e impactos da Reforma Tributária.",
      },
      { property: "og:title", content: "Diagnóstico Tributário | Análise Executiva" },
      {
        property: "og:description",
        content:
          "Diagnóstico tributário para empresas: carga, regime, margem e Reforma Tributária.",
      },
    ],
  }),
  component: Diagnostico,
});

const TOTAL = 9;

const STEP_TITLES = [
  { eyebrow: "Identificação", title: "Vamos começar pelo essencial" },
  { eyebrow: "Momento Tributário", title: "Como está sua empresa hoje" },
  { eyebrow: "Carga Tributária", title: "Quanto você paga em impostos" },
  { eyebrow: "Segmento", title: "Onde sua empresa atua" },
  { eyebrow: "Regime Tributário", title: "Como você é tributado hoje" },
  { eyebrow: "Margem de Lucro", title: "A rentabilidade da operação" },
  { eyebrow: "Estrutura de Custos", title: "Para onde vai o seu dinheiro" },
  { eyebrow: "Mão de Obra", title: "Quem executa o trabalho" },
  { eyebrow: "Perfil de Mercado", title: "Quem são seus clientes" },
];

type Form = {
  empresa: string;
  nomeResponsavel: string;
  cargoResponsavel: string;
  email: string;
  whatsapp: string;
  faturamento: string;
  momento: string;
  carga: string;
  setor: string;
  regime: string;
  margem: string;
  custos: string;
  maoDeObra: string;
  perfilClientes: string;
};

const EMPTY: Form = {
  empresa: "",
  nomeResponsavel: "",
  cargoResponsavel: "",
  email: "",
  whatsapp: "",
  faturamento: "",
  momento: "",
  carga: "",
  setor: "",
  regime: "",
  margem: "",
  custos: "",
  maoDeObra: "",
  perfilClientes: "",
};

const REQUIRED: Record<number, (keyof Form)[]> = {
  1: ["empresa", "nomeResponsavel", "cargoResponsavel", "email", "faturamento"],
  2: ["momento"],
  3: ["carga"],
  4: ["setor"],
  5: ["regime"],
  6: ["margem"],
  7: ["custos"],
  8: ["maoDeObra"],
  9: ["perfilClientes"],
};

function Diagnostico() {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (patch: Partial<Form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setError("");
  };

  const stepMeta = STEP_TITLES[step - 1]!;

  const next = async () => {
    const missing = (REQUIRED[step] ?? []).some((k) => !form[k].trim());
    if (missing) {
      setError("Preencha todos os campos desta etapa para continuar.");
      return;
    }
    setError("");

    if (step === 1) {
      setLoading(true);
      try {
        const exists = await checkEmailExists(form.email);
        if (exists) {
          toast.warning("Este e-mail já realizou o diagnóstico.", {
            description: "Cada empresa pode submeter apenas um diagnóstico por e-mail.",
            duration: 5000,
          });
          return;
        }
      } catch {
        // se a verificação falhar, permite continuar
      } finally {
        setLoading(false);
      }
    }

    if (step === TOTAL) {
      setLoading(true);
      try {
        await createDiagnostico(form);
        navigate({ to: "/obrigado" });
      } catch {
        setError("Erro ao enviar o diagnóstico. Verifique sua conexão e tente novamente.");
      } finally {
        setLoading(false);
      }
      return;
    }
    setDir(1);
    setStep((s) => s + 1);
  };

  const back = () => {
    if (step === 1) return;
    setDir(-1);
    setError("");
    setStep((s) => s - 1);
  };

  return (
    <AnimatePresence mode="wait">
      {showIntro ? (
        <IntroScreen key="intro" onStart={() => setShowIntro(false)} />
      ) : (
        <motion.main
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="min-h-screen px-4 py-8 sm:px-6 sm:py-12"
        >
          <div className="mx-auto w-full max-w-3xl">
            <header className=" flex items-center justify-between gap-4">
              <img src={logo} alt="Rocha Maia Advogados" className="h-32 w-auto sm:h-30" />
            </header>

            <section className="glass-panel relative overflow-hidden rounded-3xl p-5 sm:p-9">
              <div className="absolute inset-x-0 top-0 h-0.5 rounded-t-3xl gradient-primary" />
              <div className="mb-8">
                <div className="mb-3 flex items-end justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">
                      {stepMeta.eyebrow}
                    </p>
                    <h1 className="mt-2 text-xl font-bold sm:text-2xl">{stepMeta.title}</h1>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">
                    Etapa {step} de {TOTAL}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                  <motion.div
                    className="h-full rounded-full gradient-primary"
                    initial={false}
                    animate={{ width: `${(step / TOTAL) * 100}%` }}
                    transition={{ type: "spring", stiffness: 160, damping: 24 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait" initial={false} custom={dir}>
                <motion.div
                  key={step}
                  custom={dir}
                  initial={{ opacity: 0, x: dir * 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: dir * -40 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="space-y-8"
                >
                  {step === 1 && (
                    <div className="space-y-5">
                      <Field
                        label="Nome da empresa"
                        value={form.empresa}
                        onChange={(e) => set({ empresa: e.target.value })}
                      />
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field
                          label="Nome do responsável"
                          value={form.nomeResponsavel}
                          onChange={(e) => set({ nomeResponsavel: e.target.value })}
                        />
                        <Field
                          label="Cargo"
                          value={form.cargoResponsavel}
                          onChange={(e) => set({ cargoResponsavel: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field
                          label="E-mail"
                          type="email"
                          inputMode="email"
                          value={form.email}
                          onChange={(e) => set({ email: e.target.value })}
                        />
                        <Field
                          label="WhatsApp"
                          type="tel"
                          inputMode="tel"
                          value={form.whatsapp}
                          onChange={(e) => set({ whatsapp: formatPhone(e.target.value) })}
                        />
                      </div>
                      <Field
                        label="Faturamento bruto anual aproximado"
                        inputMode="numeric"
                        value={form.faturamento}
                        onChange={(e) => set({ faturamento: formatCurrency(e.target.value) })}
                      />
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <QuestionTitle>
                        Qual frase melhor descreve o momento da empresa em relação à Reforma
                        Tributária?
                      </QuestionTitle>
                      <div className="grid gap-3">
                        {MOMENTOS.map((o) => (
                          <OptionCard
                            key={o}
                            label={o}
                            selected={form.momento === o}
                            onSelect={() => set({ momento: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div>
                      <QuestionTitle>
                        Carga tributária total atual em relação ao faturamento?
                      </QuestionTitle>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {CARGAS.map((o) => (
                          <OptionCard
                            key={o}
                            compact
                            label={o}
                            selected={form.carga === o}
                            onSelect={() => set({ carga: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div>
                      <QuestionTitle>Principal setor ou segmento de atuação?</QuestionTitle>
                      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                        {SETORES.map((o) => (
                          <OptionCard
                            key={o}
                            compact
                            label={o}
                            selected={form.setor === o}
                            onSelect={() => set({ setor: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 5 && (
                    <div>
                      <QuestionTitle>Regime tributário atual?</QuestionTitle>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {REGIMES.map((o) => (
                          <OptionCard
                            key={o}
                            compact
                            label={o}
                            selected={form.regime === o}
                            onSelect={() => set({ regime: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div>
                      <QuestionTitle>
                        Margem de lucro média estimada antes de IRPJ/CSLL?
                      </QuestionTitle>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {MARGENS.map((o) => (
                          <OptionCard
                            key={o}
                            compact
                            label={o}
                            selected={form.margem === o}
                            onSelect={() => set({ margem: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 7 && (
                    <div>
                      <QuestionTitle>Onde está a maior parte dos custos e despesas?</QuestionTitle>
                      <div className="grid gap-3">
                        {CUSTOS.map((o) => (
                          <OptionCard
                            key={o}
                            label={o}
                            selected={form.custos === o}
                            onSelect={() => set({ custos: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 8 && (
                    <div>
                      <QuestionTitle>Situação predominante da mão de obra?</QuestionTitle>
                      <div className="grid gap-3">
                        {MAO_DE_OBRA.map((o) => (
                          <OptionCard
                            key={o}
                            label={o}
                            selected={form.maoDeObra === o}
                            onSelect={() => set({ maoDeObra: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 9 && (
                    <div>
                      <QuestionTitle>Perfil predominante dos clientes?</QuestionTitle>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {PERFIS.map((o) => (
                          <OptionCard
                            key={o}
                            compact
                            label={o}
                            selected={form.perfilClientes === o}
                            onSelect={() => set({ perfilClientes: o })}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {error ? <p className="mt-6 text-sm text-destructive">{error}</p> : null}

              <div className="mt-9 flex items-center justify-between gap-3 border-t border-border pt-6">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 1}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
                >
                  <i className="fi fi-tr-arrow-left leading-none" /> Anterior
                </button>
                <motion.button
                  type="button"
                  onClick={next}
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="inline-flex items-center gap-2 rounded-xl gradient-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-primary disabled:opacity-70 disabled:pointer-events-none"
                >
                  {loading
                    ? step === TOTAL
                      ? "Enviando…"
                      : "Verificando…"
                    : step === TOTAL
                      ? "Finalizar diagnóstico"
                      : "Próximo"}
                  {!loading &&
                    (step === TOTAL ? (
                      <i className="fi fi-tr-sparkles leading-none" />
                    ) : (
                      <i className="fi fi-tr-arrow-right leading-none" />
                    ))}
                </motion.button>
              </div>
            </section>

            <p className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <i className="fi fi-tr-shield-trust text-sm leading-none text-primary" />
              Seus dados são tratados com confidencialidade executiva.
            </p>
          </div>
        </motion.main>
      )}
    </AnimatePresence>
  );
}

const introContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.15 } },
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
} as const;

const introItem = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
} as const;

function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <motion.main
      variants={introContainer}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="flex min-h-screen flex-col items-center justify-center px-6 text-center"
    >
      <motion.img
        variants={introItem}
        src={logo}
        alt="Rocha Maia Advogados"
        className=" h-52 w-auto sm:h-60"
      />

      <motion.div variants={introItem} className="mb-5 flex items-center justify-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/35 bg-primary/8 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80">
          <svg
            className="size-3 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="3" x2="12" y2="21" />
            <path d="M17 5H7" />
            <path d="M5.5 5 3 11a2.5 2.5 0 0 0 5 0L5.5 5Z" />
            <path d="M18.5 5 16 11a2.5 2.5 0 0 0 5 0L18.5 5Z" />
            <path d="M9 21h6" />
          </svg>
          Advocacia Tributária
        </span>
      </motion.div>

      <motion.p
        variants={introItem}
        className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary"
      >
        Bem-vindo ao
      </motion.p>

      <motion.h1
        variants={introItem}
        className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl"
      >
        Diagnóstico Preliminar
        <br />
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "var(--gradient-primary)" }}
        >
          de Reforma Tributária
        </span>
      </motion.h1>

      <motion.p
        variants={introItem}
        className="mt-5 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/60"
      >
        ROCHA MAIA · Advogados
      </motion.p>

      <motion.p
        variants={introItem}
        className="mt-7 max-w-[340px] text-sm leading-relaxed text-muted-foreground"
      >
        Responda algumas perguntas sobre a sua empresa e descubra como a Reforma Tributária impacta
        o seu negócio.
      </motion.p>

      <motion.div variants={introItem} className="mt-10">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onStart}
          className="inline-flex items-center gap-3 rounded-2xl gradient-primary px-8 py-4 text-sm font-semibold text-primary-foreground glow-primary"
        >
          Iniciar diagnóstico
          <i className="fi fi-tr-arrow-right leading-none" />
        </motion.button>
      </motion.div>
    </motion.main>
  );
}
